import { useEffect, useMemo, useState } from "react"
import { type Query as TCubeQuery, ResultSet } from "@cubejs-client/core"

import { CUBE_QUERY_KEYS_MAP, getCubeQueryBase } from "../../constants"
import useCampaignAdSetAdDetails, {
  prepareAllItemsDetails,
  useAllCampaignsAdSetsAdsDetails,
} from "~/ui-rtk/hooks/campaign-adset-ad-details"
import {
  getPivotData,
  polyfillCubeQueryWithTimeDimension,
} from "~/ui-rtk/utils/cube"

import { MetricsMapping } from "~/ui-rtk/constants/metrics-mapping"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { useYearDateRange } from "~/ui-rtk/hooks/date-range"

const defaultPrefixKey = "company_media_metrics_campaigns"

const requests: Record<string, TCubeQuery> = {}
const results: Record<string, Record<string, unknown>> = {}

export const useAllCampaignsCubeQuery = () => {
  const { postProcess } = MetricsMapping.brand_media_optimization_campaign_table
  const prefixKey = defaultPrefixKey
  const currentCompany = useCurrentCompany()
  const currentCompanyId = currentCompany?.id ?? "-company-not-set-"
  const cacheKey = `${prefixKey}.${currentCompanyId}`
  const queryAllCampaignKey = `${cacheKey}.all`
  const campaignIdKey = `${prefixKey}.campaign_id`
  const purchaseKey = `${prefixKey}.purchases`
  const dateRange = useYearDateRange()

  const allCampaignsQuery = useMemo(() => {
    if (!requests[queryAllCampaignKey]) {
      const base = getCubeQueryBase(prefixKey)
      requests[queryAllCampaignKey] = {
        ...base,
        measures: [...base.measures, purchaseKey],
        dimensions: [campaignIdKey],
      }
    }
    return polyfillCubeQueryWithTimeDimension(requests[queryAllCampaignKey], {
      dateRange,
    })
  }, [queryAllCampaignKey, prefixKey, purchaseKey, campaignIdKey, dateRange])

  const { [CUBE_QUERY_KEYS_MAP.ALL]: allCampaignsSet, isLoading } =
    useAllCampaignsAdSetsAdsDetails(allCampaignsQuery)

  if (!results[cacheKey]) {
    results[cacheKey] = {}
  }

  const [allCampaignsDetails, setAllCampaignsDetails] = useState<
    ReturnType<typeof prepareAllItemsDetails> | undefined
  >(
    results[cacheKey]?.allCampaignsDetails as
      | ReturnType<typeof prepareAllItemsDetails>
      | undefined,
  )

  useEffect(() => {
    if (!allCampaignsSet) return undefined
    let cancelled = false
    const run = async () => {
      const pivotData = getPivotData(
        allCampaignsSet as ResultSet<Record<string, unknown>>,
        allCampaignsQuery,
      )
      const processedPivotData = postProcess
        ? (await Promise.resolve(postProcess(pivotData, {})))[0]
        : pivotData
      if (cancelled) return
      const prepared = prepareAllItemsDetails(processedPivotData)
      results[cacheKey].allCampaignsDetails = prepared
      setAllCampaignsDetails(prepared)
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [allCampaignsSet, allCampaignsQuery, cacheKey, postProcess])

  return { allCampaignsDetails, isLoading }
}

export const useCampaignQubeQuery = () => {
  const { postProcess } = MetricsMapping.brand_media_optimization_campaign_table
  const prefixKey = defaultPrefixKey
  const currentCompany = useCurrentCompany()
  const currentCompanyId = currentCompany?.id ?? "-company-not-set-"
  const cacheKey = `${prefixKey}.${currentCompanyId}`
  const queryAllCampaignKey = `${cacheKey}.all`
  const dateRange = useYearDateRange()

  const campaignIdKey = `${prefixKey}.campaign_id`
  const purchaseKey = `${prefixKey}.purchases`

  const allCampaignsQuery = useMemo(() => {
    if (!requests[queryAllCampaignKey]) {
      const base = getCubeQueryBase(prefixKey)
      requests[queryAllCampaignKey] = {
        ...base,
        measures: [...base.measures, purchaseKey],
        dimensions: [campaignIdKey],
      }
    }
    return polyfillCubeQueryWithTimeDimension(requests[queryAllCampaignKey], {
      dateRange,
    })
  }, [queryAllCampaignKey, prefixKey, purchaseKey, campaignIdKey, dateRange])

  const { [CUBE_QUERY_KEYS_MAP.ALL]: allCampaignsSet, isLoading } =
    useCampaignAdSetAdDetails({
      [CUBE_QUERY_KEYS_MAP.ALL]: allCampaignsQuery,
    })

  if (!results[cacheKey]) {
    results[cacheKey] = {}
  }

  const [allCampaignsDetails, setAllCampaignsDetails] = useState<
    ReturnType<typeof prepareAllItemsDetails> | undefined
  >(
    results[cacheKey]?.allCampaignsDetails as
      | ReturnType<typeof prepareAllItemsDetails>
      | undefined,
  )

  useEffect(() => {
    if (!allCampaignsSet || isLoading) return undefined
    let cancelled = false
    const run = async () => {
      const pivotData = getPivotData(
        allCampaignsSet as ResultSet<Record<string, unknown>>,
        allCampaignsQuery,
      )
      const processedData = postProcess
        ? (await Promise.resolve(postProcess(pivotData, {})))[0]
        : pivotData
      if (cancelled) return
      const prepared = prepareAllItemsDetails(processedData)
      results[cacheKey].allCampaignsDetails = prepared
      setAllCampaignsDetails(prepared)
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [allCampaignsSet, isLoading, allCampaignsQuery, cacheKey, postProcess])

  return { allCampaignsDetails, isLoading }
}
